"use client";

import {
  readUserErrorI18n,
  readUserErrorMessage,
} from "@v1/backend/logic/errors/user_error";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { humanizeMessageKey } from "./message-fallback";

/**
 * Turns a backend error into text in the user's language.
 *
 * Resolution order, and why:
 *  1. `data.i18n.key` — the backend said which message this is, so it can be
 *     shown in whatever language the user reads.
 *  2. `data.message` or a string `data` — German / legacy payload. Never use
 *     `error.message` (Convex stringifies the payload to JSON).
 *  3. the caller's fallback — for errors that never reached the backend at all
 *     (offline, aborted request).
 *
 * A key that is missing from the catalog falls through to the German message
 * rather than rendering something meaningless at the user:
 * `scripts/check-i18n.mjs` is where a missing key gets caught, not the screen.
 *
 * Detecting "missing" needs care. This app configures
 * `getMessageFallback={humanizeMessageKey}`, so next-intl does NOT echo the key
 * back — it returns a humanised last segment. `errors.inboxAgent.xy` came out
 * as „Xy", English-looking and meaningless, and the German message below was
 * never reached because the old check compared against the raw key. Comparing
 * against the very fallback the provider is configured with is what closes
 * that: whatever `getMessageFallback` would produce means the catalog had
 * nothing.
 */
export function useServerErrorMessage(): (
  error: unknown,
  fallback: string,
) => string {
  // Root namespace: backend keys are fully qualified ("errors.invoices.locked"),
  // because the backend has no notion of which UI screen will show them.
  const t = useTranslations();

  return useCallback(
    (error: unknown, fallback: string): string => {
      const data =
        error !== null && typeof error === "object" && "data" in error
          ? (error as { data?: unknown }).data
          : undefined;

      const i18n = readUserErrorI18n(data);
      if (i18n !== null) {
        const translated = t(i18n.key, i18n.params);
        const missing: boolean =
          translated === i18n.key ||
          translated === humanizeMessageKey({ key: i18n.key });
        if (!missing) return translated;
      }

      const german = readUserErrorMessage(data);
      if (german !== null) return german;

      return fallback;
    },
    [t],
  );
}
